import { useEffect, useMemo, useState } from 'react';
import type { AppContext } from '@/lib/core/types';
import { useClientTranslation, useLocaleStore } from '@/lib/core';
import { formatShortDate } from '@/lib/core/utils';
import type { CheckoutContentResponseModel } from '@/services/platform/api/model';
import type { MewsBookingState } from '@/services/platform/data/booking/mews/types';
import { CheckoutContexts } from '@/services/platform/data/checkout';
import { getMewsProducts, resolveProductName } from '@/services/platform/data/booking/mews';
import type { MewsProductDto } from '@/services/platform/data/booking/mews';
import { CheckoutScreen } from './CheckoutScreen';
import type { CheckoutPurchase } from './types';

interface MewsPaymentStepProps {
  /**
   * Both the payment's context reference and what the caller re-fetches once payment lands.
   *
   * Undefined means there is nothing to pay for — a deep link or a refresh lost the reservation.
   * `CheckoutScreen` owns that recovery, so this still renders and simply hands it no purchase.
   */
  reservationGuid: string | undefined;
  /**
   * The same total already shown to the guest during booking. It travels because
   * `ReservationDetailDto` carries no amount field; the server re-resolves it independently and
   * never trusts this.
   */
  amountMajor: number | undefined;
  currencyCode?: string;
  /** The flow's own booking state, already revived (`dateFrom`/`dateTo` as real `Date`s). */
  booking: MewsBookingState | undefined;
  context?: AppContext;
  content?: CheckoutContentResponseModel;
  /**
   * Where `CheckoutScreen` navigates on back when `onBack` is not given — i.e. the route case only.
   * An embedding flow leaves this alone; `onBack` overrides every navigation-away path it has.
   */
  returnPath?: string;
  /** Where "back" goes — a route navigation for `MewsCheckoutScreen`, a step change for a flow. */
  onBack?: () => void;
  onSuccess: () => void;
  /** Forwarded to `CheckoutScreen` — set by flows that render this as one of their own steps. */
  embedded?: boolean;
}

/**
 * Payment for a Mews-backed booking (sunbed, stay, the Mews branch of experience), over the generic
 * `CheckoutScreen`.
 *
 * Rendered two ways, which is why it takes a reservation rather than a router/session state shape:
 * inline as a booking flow's payment step, and — via `MewsCheckoutScreen` — as the dedicated
 * `/checkout` route. Both need the identical purchase; only "where back goes" and "what success
 * means" differ, and those are the caller's two props.
 *
 * By the time either reaches here the reservation already exists and is held (`AwaitingPayment`) —
 * the flow's `handleBookNow` only gets this far for a reservation that needs paying for — so success
 * does not create anything, it just says the money landed.
 */
export function MewsPaymentStep({
  reservationGuid,
  amountMajor,
  currencyCode,
  booking,
  context,
  content,
  returnPath = '/',
  onBack,
  onSuccess,
  embedded,
}: MewsPaymentStepProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);

  const [products, setProducts] = useState<MewsProductDto[]>([]);
  useEffect(() => {
    if (!booking?.extras.length) return;
    getMewsProducts(
      {
        CurrencyCode: 'EUR',
        ...(booking.venue?.externalId ? { EnterpriseId: booking.venue.externalId } : {}),
        ...(booking.venue?.bookableServiceExternalId
          ? { ServiceId: booking.venue.bookableServiceExternalId }
          : {}),
      },
      locale
    ).then(data => setProducts(data ?? []));
  }, [locale, booking?.extras, booking?.venue?.externalId, booking?.venue?.bookableServiceExternalId]);

  const purchase: CheckoutPurchase | undefined = useMemo(() => {
    if (!reservationGuid || !booking) return undefined;

    const dateRange =
      booking.dateFrom && booking.dateTo && booking.dateFrom.toDateString() !== booking.dateTo.toDateString()
        ? `${formatShortDate(booking.dateFrom)} – ${formatShortDate(booking.dateTo)}`
        : booking.dateFrom
          ? formatShortDate(booking.dateFrom)
          : '';

    const howManyValue = [
      booking.adults > 0
        ? `${booking.adults} ${booking.adults === 1 ? t('App.Labels.SunbedAdult') : t('App.Labels.SunbedAdults')}`
        : null,
      booking.children > 0 ? `${booking.children} ${t('App.Labels.SunbedChildren')}` : null,
    ]
      .filter(Boolean)
      .join(' + ');

    const categoryRows = booking.allocations.map(alloc => {
      const totalAdults = alloc.guestDistribution.reduce((sum, g) => sum + g.adults, 0);
      const totalChildren = alloc.guestDistribution.reduce((sum, g) => sum + g.children, 0);

      return {
        label: t('App.Labels.SunbedSummaryWhichCategory'),
        value: `x${alloc.quantity} ${alloc.categoryName}`,
        hint: [
          totalAdults > 0 ? `${t('App.Labels.SunbedAdults')}: ${totalAdults}` : null,
          totalChildren > 0 ? `${t('App.Labels.SunbedChildren')}: ${totalChildren}` : null,
        ]
          .filter(Boolean)
          .join('<br/>'),
      };
    });

    const addonNames = booking.extras.length
      ? products
          .filter(p => p.id && booking.extras.includes(p.id))
          .map(p => resolveProductName(p))
          .filter(Boolean)
      : [];

    return {
      context: CheckoutContexts.MewsBooking,
      contextReference: reservationGuid,
      amountMajor: amountMajor ?? 0,
      currencyCode,
      returnPath,
      summaryRows: [
        ...(booking.venue?.name ? [{ label: t('App.Checkout.Where'), value: booking.venue.name }] : []),
        ...(dateRange ? [{ label: t('App.Checkout.When'), value: dateRange }] : []),
        ...(howManyValue ? [{ label: t('App.Labels.HowMany'), value: howManyValue }] : []),
        ...categoryRows,
        ...(addonNames.length > 0
          ? [{ label: t('App.Labels.Addons'), value: addonNames.join('<br/>') }]
          : []),
      ],
    };
  }, [reservationGuid, amountMajor, currencyCode, returnPath, booking, products, t]);

  return (
    <CheckoutScreen
      content={content}
      context={context}
      purchase={purchase}
      onSucceeded={onSuccess}
      onBack={onBack}
      embedded={embedded}
    />
  );
}
